import React, { useState, useEffect } from 'react';
import { Mosque } from '../types/mosque';
import { FilterOptions } from './SearchFilter';
import { useLanguage } from '../contexts/LanguageContext';

interface SidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  mosques: Mosque[];
  onMosqueClick: (mosque: Mosque) => void;
  onSearch: (query: string) => void;
  onFilter: (filters: FilterOptions) => void;
  userLocation?: { lat: number; lng: number } | null;
  isLoading?: boolean;
  isAdmin: boolean;
  onAdminClick: () => void;
}

// Haversine formula to calculate distance
const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const SidePanel: React.FC<SidePanelProps> = ({
  isOpen,
  onClose,
  mosques,
  onMosqueClick,
  onSearch,
  onFilter,
  userLocation,
  isLoading,
  isAdmin,
  onAdminClick,
}) => {
  const { t } = useLanguage();
  const ITEMS_PER_PAGE = 10;
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<FilterOptions>({
    type: 'all',
    isPublic: 'all',
    facilities: {
      menWc: false,
      womenWc: false,
      menWudu: false,
      womenWudu: false,
      womenPrayerArea: false,
    },
  });

  // Close panel on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    setCurrentPage(1);
    onSearch(query);
  };

  const handleFilterChange = (key: keyof FilterOptions, value: string) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    setCurrentPage(1);
    onFilter(newFilters);
  };

  const handleFacilityFilterChange = (facility: keyof FilterOptions['facilities']) => {
    const newFilters = {
      ...filters,
      facilities: {
        ...filters.facilities,
        [facility]: !filters.facilities[facility],
      },
    };
    setFilters(newFilters);
    setCurrentPage(1);
    onFilter(newFilters);
  };

  const clearFilters = () => {
    const defaultFilters: FilterOptions = {
      type: 'all',
      isPublic: 'all',
      facilities: {
        menWc: false,
        womenWc: false,
        menWudu: false,
        womenWudu: false,
        womenPrayerArea: false,
      },
    };
    setFilters(defaultFilters);
    setCurrentPage(1);
    onFilter(defaultFilters);
    setSearchQuery('');
    onSearch('');
  };

  // Sort mosques by distance if user location is available
  const sortedMosques = [...mosques].sort((a, b) => {
    if (userLocation) {
      const distA = calculateDistance(
        userLocation.lat,
        userLocation.lng,
        a.location.lat,
        a.location.lng
      );
      const distB = calculateDistance(
        userLocation.lat,
        userLocation.lng,
        b.location.lat,
        b.location.lng
      );
      return distA - distB;
    }
    return a.name.localeCompare(b.name);
  });

  const totalPages = Math.max(1, Math.ceil(sortedMosques.length / ITEMS_PER_PAGE));
  const paginatedMosques = sortedMosques.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleMosqueItemClick = (mosque: Mosque) => {
    onMosqueClick(mosque);
    // Close panel on mobile after selection
    if (window.innerWidth < 768) {
      onClose();
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'mosque': return '#6366f1';
      case 'prayer_room': return '#14b8a6';
      case 'hired_hall': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const formatDistance = (mosque: Mosque) => {
    if (!userLocation) return null;
    const dist = calculateDistance(
      userLocation.lat,
      userLocation.lng,
      mosque.location.lat,
      mosque.location.lng
    );
    return dist < 1 ? `${Math.round(dist * 1000)}m` : `${dist.toFixed(1)}km`;
  };

  return (
    <>
      {/* Overlay for mobile */}
      <div
        className={`side-panel-overlay ${isOpen ? 'visible' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      <aside className={`side-panel ${isOpen ? 'open' : ''}`} role="navigation">
        {/* Panel Header */}
        <div className="panel-header">
          <a href="/" className="panel-title-link">
            <h1 className="panel-title">Mosque Map</h1>
          </a>
          <button className="panel-close-btn" onClick={onClose} aria-label="Close menu">
            <span>&times;</span>
          </button>
        </div>

        {/* Search Section */}
        <div className="panel-search">
          <input
            type="text"
            placeholder={t('map.searchPlaceholder')}
            value={searchQuery}
            onChange={handleSearchChange}
            className="panel-search-input"
            data-testid="mosque-search-input"
          />
          <button
            className={`panel-filter-btn ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
            aria-expanded={showFilters}
            data-testid="mosque-filter-toggle"
          >
            <span>Filter</span>
          </button>
        </div>

        {/* Filters Section */}
        {showFilters && (
          <div className="panel-filters" data-testid="mosque-filters-panel">
            <div className="filter-group">
              <label>{t('filters.type')}:</label>
              <select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                data-testid="mosque-filter-type"
              >
                <option value="all">{t('filters.allTypes')}</option>
                <option value="mosque">{t('mosqueTypes.mosque')}</option>
                <option value="prayer_room">{t('mosqueTypes.prayerRoom')}</option>
                <option value="hired_hall">{t('mosqueTypes.hiredHall')}</option>
              </select>
            </div>

            <div className="filter-group">
              <label>{t('filters.access')}:</label>
              <select
                value={filters.isPublic}
                onChange={(e) => handleFilterChange('isPublic', e.target.value)}
                data-testid="mosque-filter-access"
              >
                <option value="all">{t('filters.all')}</option>
                <option value="public">{t('filters.publicOnly')}</option>
                <option value="private">{t('filters.privateOnly')}</option>
              </select>
            </div>

            <div className="filter-group">
              <label>{t('filters.facilities')}:</label>
              <div className="facility-filter-chips">
                {Object.entries(filters.facilities).map(([key, checked]) => (
                  <button
                    key={key}
                    className={`facility-chip ${checked ? 'active' : ''}`}
                    onClick={() => handleFacilityFilterChange(key as keyof FilterOptions['facilities'])}
                  >
                    {key === 'menWc' && t('facilities.menWc')}
                    {key === 'womenWc' && t('facilities.womenWc')}
                    {key === 'menWudu' && t('facilities.menWudu')}
                    {key === 'womenWudu' && t('facilities.womenWudu')}
                    {key === 'womenPrayerArea' && t('facilities.womenPrayerArea')}
                  </button>
                ))}
              </div>
            </div>

            <button onClick={clearFilters} className="clear-all-btn" data-testid="mosque-filter-clear">
              {t('filters.clearAll')}
            </button>
          </div>
        )}

        {/* Mosque List */}
        <div className="panel-list">
          <div className="list-count" data-testid="mosque-list-count">
            {sortedMosques.length} {sortedMosques.length === 1 ? 'mosque' : 'mosques'} found
          </div>

          {isLoading ? (
            <div className="panel-loading">
              <div className="spinner"></div>
              <p>Loading mosques...</p>
            </div>
          ) : sortedMosques.length === 0 ? (
            <div className="panel-empty">
              <p>No mosques found</p>
              <button onClick={clearFilters}>Clear filters</button>
            </div>
          ) : (
            <div className="mosque-list-items">
              {paginatedMosques.map((mosque) => (
                <div
                  key={mosque.id}
                  className="mosque-list-item"
                  onClick={() => handleMosqueItemClick(mosque)}
                  data-testid="mosque-list-item"
                >
                  <div className="mosque-item-left">
                    <h3 className="mosque-item-name">{mosque.name}</h3>
                    <div className="mosque-item-meta">
                      <span
                        className="mosque-item-type"
                        style={{ backgroundColor: getTypeColor(mosque.type) }}
                      >
                        {t(`mosqueTypes.${mosque.type === 'mosque' ? 'mosque' : mosque.type === 'prayer_room' ? 'prayerRoom' : 'hiredHall'}`)}
                      </span>
                      {mosque.isPublic ? (
                        <span className="mosque-item-public">{t('mosqueDetails.public')}</span>
                      ) : (
                        <span className="mosque-item-private">{t('mosqueDetails.private')}</span>
                      )}
                    </div>
                  </div>
                  <div className="mosque-item-right">
                    {formatDistance(mosque) && (
                      <span className="mosque-item-distance">{formatDistance(mosque)}</span>
                    )}
                    <span className="mosque-item-arrow">&rsaquo;</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isLoading && sortedMosques.length > ITEMS_PER_PAGE && (
            <div className="panel-pagination" data-testid="mosque-list-pagination">
              <button
                className="panel-pagination-btn"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                {t('admin.previous')}
              </button>
              <span className="panel-pagination-info">
                {t('admin.pageOf')
                  .replace('{current}', String(currentPage))
                  .replace('{total}', String(totalPages))
                  .replace('{count}', String(sortedMosques.length))}
              </span>
              <button
                className="panel-pagination-btn"
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                {t('admin.next')}
              </button>
            </div>
          )}
        </div>

        {/* Panel Footer */}
        {isAdmin && (
          <div className="panel-footer">
            <button className="admin-btn" onClick={onAdminClick} data-testid="admin-login-button">
              <span>{t('admin.dashboard')}</span>
            </button>
          </div>
        )}
      </aside>
    </>
  );
};

export default SidePanel;
